import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { languagesService } from '../services/api';

// Types d'actions
const ACTIONS = {
  // Collections
  SET_COLLECTIONS: 'SET_COLLECTIONS',
  ADD_COLLECTION: 'ADD_COLLECTION',
  UPDATE_COLLECTION: 'UPDATE_COLLECTION',
  DELETE_COLLECTION: 'DELETE_COLLECTION',
  SET_COLLECTIONS_LOADING: 'SET_COLLECTIONS_LOADING',
  SET_COLLECTIONS_ERROR: 'SET_COLLECTIONS_ERROR',

  // Langues
  SET_LANGUAGES: 'SET_LANGUAGES',
  SET_LANGUAGES_LOADING: 'SET_LANGUAGES_LOADING',
  SET_LANGUAGES_ERROR: 'SET_LANGUAGES_ERROR',

  // UI State
  SET_ACTIVE_COLLECTION: 'SET_ACTIVE_COLLECTION',
  SET_MODAL_OPEN: 'SET_MODAL_OPEN',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION'
};

// État initial
const initialState = {
  // Collections
  collections: [],
  collectionsLoading: false,
  collectionsError: null,
  activeCollection: null,

  // Langues
  languages: [],
  languagesLoading: false,
  languagesError: null,

  // État de l'interface
  modals: {
    collectionForm: false,
    collectionView: false,
    share: false
  },

  // Notifications
  notification: null
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_COLLECTIONS:
      return {
        ...state,
        collections: (action.payload || [])
          .filter(c => c !== null && c !== undefined && typeof c === 'object')
          .map(c => {
            // Créer un objet propre avec toutes les propriétés
            const clean = { ...c };
            // Assurer que isPublic existe
            clean.isPublic = (c.hasOwnProperty('isPublic') && c.isPublic === true) ? true : false;
            return clean;
          }),
        collectionsLoading: false,
        collectionsError: null
      };

    case ACTIONS.ADD_COLLECTION:
      const newCollection = { ...(action.payload || {}) };
      // Assurer que isPublic existe
      newCollection.isPublic = (action.payload?.isPublic === true) ? true : false;
      return {
        ...state,
        collections: [...state.collections, newCollection]
      };

    case ACTIONS.UPDATE_COLLECTION:
      return {
        ...state,
        collections: state.collections.map(collection => {
          if (collection && collection.id === action.payload.id) {
            const updated = { ...collection, ...action.payload.updates };
            // S'assurer que isPublic est toujours défini
            if (updated.isPublic === undefined) {
              updated.isPublic = false;
            }
            return updated;
          }
          return collection;
        })
      };

    case ACTIONS.DELETE_COLLECTION:
      return {
        ...state,
        collections: state.collections.filter(collection => collection.id !== action.payload)
      };

    case ACTIONS.SET_COLLECTIONS_LOADING:
      return {
        ...state,
        collectionsLoading: action.payload
      };

    case ACTIONS.SET_COLLECTIONS_ERROR:
      return {
        ...state,
        collectionsError: action.payload,
        collectionsLoading: false
      };

    case ACTIONS.SET_LANGUAGES:
      return {
        ...state,
        languages: action.payload,
        languagesLoading: false,
        languagesError: null
      };

    case ACTIONS.SET_LANGUAGES_LOADING:
      return {
        ...state,
        languagesLoading: action.payload
      };

    case ACTIONS.SET_LANGUAGES_ERROR:
      return {
        ...state,
        languagesError: action.payload,
        languagesLoading: false
      };

    case ACTIONS.SET_ACTIVE_COLLECTION:
      return {
        ...state,
        activeCollection: action.payload
      };

    case ACTIONS.SET_MODAL_OPEN:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload.modal]: action.payload.open
        }
      };

    case ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: action.payload
      };

    case ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null
      };

    default:
      return state;
  }
};

// Contexte
const AppContext = createContext();

// Provider
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Charger les langues depuis l'API
  React.useEffect(() => {
    const fetchLanguages = async () => {
      try {
        dispatch({ type: ACTIONS.SET_LANGUAGES_LOADING, payload: true });
        
        // Utiliser le service API pour charger les langues
        const result = await languagesService.getAll();
        
        let languagesData = [];
        if (result && result.success && Array.isArray(result.data)) {
          languagesData = result.data;
        } else if (Array.isArray(result)) {
          languagesData = result;
        }
        
        if (languagesData.length > 0) {
          dispatch({ type: ACTIONS.SET_LANGUAGES, payload: languagesData });
        } else {
          // Fallback si l'API ne retourne rien
          const defaultLanguages = [
            { code: 'wo', name: 'Wolof', flag: '🇸🇳', alphabet: 'Latin', active: true },
            { code: 'fr', name: 'Français', flag: '🇫🇷', alphabet: 'Latin', active: true },
            { code: 'en', name: 'English', flag: '🇺🇸', alphabet: 'Latin', active: true }
          ];
          dispatch({ type: ACTIONS.SET_LANGUAGES, payload: defaultLanguages });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des langues:', error);
        dispatch({ type: ACTIONS.SET_LANGUAGES_ERROR, payload: error.message });
      }
    };

    fetchLanguages();
  }, []);

  // Actions pour les collections
  const setCollections = useCallback((collections) => {
    dispatch({ type: ACTIONS.SET_COLLECTIONS, payload: collections });
  }, []);

  const addCollection = useCallback((collection) => {
    dispatch({ type: ACTIONS.ADD_COLLECTION, payload: collection });
  }, []);

  const updateCollection = useCallback((id, updates) => {
    dispatch({ type: ACTIONS.UPDATE_COLLECTION, payload: { id, updates } });
  }, []);

  const deleteCollection = useCallback((id) => {
    dispatch({ type: ACTIONS.DELETE_COLLECTION, payload: id });
  }, []);

  const setCollectionsLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_COLLECTIONS_LOADING, payload: loading });
  }, []);

  const setCollectionsError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_COLLECTIONS_ERROR, payload: error });
  }, []);

  // Actions pour les langues
  const setLanguages = useCallback((languages) => {
    dispatch({ type: ACTIONS.SET_LANGUAGES, payload: languages });
  }, []);

  const setLanguagesLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LANGUAGES_LOADING, payload: loading });
  }, []);

  const setLanguagesError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_LANGUAGES_ERROR, payload: error });
  }, []);

  // Actions pour l'état de l'interface
  const setActiveCollection = useCallback((collection) => {
    dispatch({ type: ACTIONS.SET_ACTIVE_COLLECTION, payload: collection });
  }, []);

  const setModalOpen = useCallback((modal, open) => {
    dispatch({ type: ACTIONS.SET_MODAL_OPEN, payload: { modal, open } });
  }, []);

  // Actions pour les notifications
  const showNotification = useCallback((message, severity = 'info', duration = 5000) => {
    dispatch({
      type: ACTIONS.SET_NOTIFICATION,
      payload: { message, severity, duration }
    });

    // Auto-fermeture de la notification
    if (duration > 0) {
      setTimeout(() => {
        dispatch({ type: ACTIONS.CLEAR_NOTIFICATION });
      }, duration);
    }
  }, []);

  const clearNotification = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_NOTIFICATION });
  }, []);

  // Utilitaires pour les langues
  const getLanguageByCode = useCallback((code) => {
    return state.languages.find(lang => lang.code === code);
  }, [state.languages]);

  const getLanguageName = useCallback((code) => {
    const language = getLanguageByCode(code);
    return language ? language.name : code;
  }, [getLanguageByCode]);

  const getLanguageFlag = useCallback((code) => {
    const language = getLanguageByCode(code);
    return language ? language.flag : '🌐';
  }, [getLanguageByCode]);

  // Sélecteurs
  const getCollectionById = useCallback((id) => {
    return state.collections.find(collection => collection.id === id);
  }, [state.collections]);

  const getCollectionsByCategory = useCallback((category) => {
    return state.collections.filter(collection => collection.category === category);
  }, [state.collections]);

  // Valeur du contexte
  const contextValue = {
    // État
    ...state,

    // Actions - Collections
    setCollections,
    addCollection,
    updateCollection,
    deleteCollection,
    setCollectionsLoading,
    setCollectionsError,

    // Actions - Langues
    setLanguages,
    setLanguagesLoading,
    setLanguagesError,

    // Actions - Interface
    setActiveCollection,
    setModalOpen,
    showNotification,
    clearNotification,

    // Utilitaires
    getLanguageByCode,
    getLanguageName,
    getLanguageFlag,
    getCollectionById,
    getCollectionsByCategory,

    // Computed values
    totalCollections: state.collections.length,
    publicCollections: state.collections.filter(c => c && c.isPublic === true).length
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook pour utiliser le contexte
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Hooks spécialisés
export const useCollections = () => {
  const {
    collections,
    collectionsLoading,
    collectionsError,
    setCollections,
    addCollection,
    updateCollection,
    deleteCollection,
    setCollectionsLoading,
    setCollectionsError,
    getCollectionById,
    getCollectionsByCategory,
    totalCollections,
    publicCollections
  } = useApp();

  return {
    collections,
    loading: collectionsLoading,
    error: collectionsError,
    setCollections,
    addCollection,
    updateCollection,
    deleteCollection,
    setLoading: setCollectionsLoading,
    setError: setCollectionsError,
    getCollectionById,
    getCollectionsByCategory,
    totalCollections,
    publicCollections
  };
};

export const useLanguages = () => {
  const {
    languages,
    languagesLoading,
    languagesError,
    setLanguages,
    setLanguagesLoading,
    setLanguagesError,
    getLanguageByCode,
    getLanguageName,
    getLanguageFlag
  } = useApp();

  return {
    languages,
    loading: languagesLoading,
    error: languagesError,
    setLanguages,
    setLoading: setLanguagesLoading,
    setError: setLanguagesError,
    getLanguageByCode,
    getLanguageName,
    getLanguageFlag
  };
};

export const useNotifications = () => {
  const { notification, showNotification, clearNotification } = useApp();

  return {
    notification,
    showNotification,
    clearNotification
  };
};

export const useModals = () => {
  const { modals, setModalOpen, activeCollection, setActiveCollection } = useApp();

  return {
    modals,
    setModalOpen,
    activeCollection,
    setActiveCollection
  };
};